// Centralized placeholder configuration for metaPairs rows per card type.
// Keep MetaRow as a pure render component; use this helper to inject missing rows.

export type CardKind = 'poi' | 'restaurant' | 'lodging' | 'shopping' | string;

export interface PlaceholderRow {
  label: string;
  value: string;
  aliases?: string[]; // optional alternate labels that count as the same field
  isLink?: boolean;   // whether this row should be treated as a link (icon-only in view mode)
}

// Common alias match helpers
export const isWebsiteLabel = (lbl?: string) => {
  if (!lbl) return false;
  const s = String(lbl).trim().toLowerCase();
  const aliases = [
    '网站','網站','网址','網址','官网','官網','官方網站','官方网站',
    'website','web','url','site','homepage','home page','link','链接','連結',
    // treat 平台聚合一栏为链接型
    '网络平台','網絡平台','平台','社交平台'
  ];
  return aliases.some(a => s === String(a).toLowerCase());
};

const isOneOf = (lbl: string | undefined, aliases: string[]) => {
  if (!lbl) return false;
  const s = String(lbl).trim().toLowerCase();
  return aliases.some(a => s === String(a).toLowerCase());
};

export const isPhoneLabel = (lbl?: string) => isOneOf(lbl, ['電話','电话','phone','tel','電話：','电话：']);
export const isOpeningHoursLabel = (lbl?: string) => isOneOf(lbl, ['營業時間','营业时间','開放時間','开放时间','opening hours','hours','營業時段','营业时段']);
export const isPriceLabel = (lbl?: string) => isOneOf(lbl, ['參考價位','参考价位','人均','均消','price','price range','price_range','平均消費','平均消费']);
export const isSignatureDishesLabel = (lbl?: string) => isOneOf(lbl, ['推薦菜','推荐菜','招牌菜','招牌']);
export const isBookingLabel = (lbl?: string) => isOneOf(lbl, ['預約','预约','訂位','订位','booking','reserve','reservation']);

// User-specified structures (2025-11-02)
// 餐厅：建议时长、参考价位、网络平台、电话、餐厅名称、菜系、推荐菜、预订建议
const RESTAURANT_PLACEHOLDERS: PlaceholderRow[] = [
  { label: '建议时长', value: '', aliases: ['建議時長','duration','duration minutes','duration_minutes'] },
  { label: '参考价位', value: '', aliases: ['參考價位','人均','均消','price','price range','price_range','平均消费','平均消費'] },
  { label: '网络平台', value: '', aliases: ['網站','网站','网址','網址','官网','官網','link','website','url','site','homepage','链接','連結'], isLink: true },
  { label: '电话', value: '', aliases: ['電話','phone','tel'] },
  { label: '餐厅名称', value: '', aliases: ['餐廳名稱','店名','店铺名称','店鋪名稱','店名/店家'] },
  { label: '菜系', value: '', aliases: ['料理','类型','類型','cuisine'] },
  { label: '推荐菜', value: '', aliases: ['推薦菜','招牌菜','招牌','signature dishes'] },
  { label: '预订建议', value: '', aliases: ['預訂建議','預約建議','预约建议','booking note','booking tips'] },
];

// POI：建议时长、参考价位、网络平台、景点名称、特色推荐、游玩提示、营业时间、最佳季节
const POI_PLACEHOLDERS: PlaceholderRow[] = [
  { label: '建议时长', value: '', aliases: ['建議時長','duration','duration minutes','duration_minutes'] },
  { label: '参考价位', value: '', aliases: ['參考價位','人均','均消','price','price range','price_range','平均消费','平均消費'] },
  { label: '网络平台', value: '', aliases: ['網站','网站','网址','網址','官网','官網','link','website','url','site','homepage','链接','連結'], isLink: true },
  { label: '景点名称', value: '', aliases: ['景點名稱','名稱','名称'] },
  { label: '特色推荐', value: '', aliases: ['特色推薦','特色','亮点','亮點','推薦','推荐'] },
  { label: '游玩提示', value: '', aliases: ['遊玩提示','遊玩建議','游玩建議','tips','注意事項','注意事项'] },
  { label: '营业时间', value: '', aliases: ['營業時間','開放時間','开放时间','opening hours','hours','營業時段','营业时段'] },
  { label: '最佳季节', value: '', aliases: ['最佳季節','適宜季節','适宜季节','季节','季節'] },
];

// Shopping：建议时长、消费水平、网络平台、购物地点、购物类型、特色推荐、营业时间、支付方式
const SHOPPING_PLACEHOLDERS: PlaceholderRow[] = [
  { label: '建议时长', value: '', aliases: ['建議時長','duration','duration minutes','duration_minutes'] },
  { label: '消费水平', value: '', aliases: ['消費水平','價格水平','价位','等級','等级'] },
  { label: '网络平台', value: '', aliases: ['網站','网站','网址','網址','官网','官網','link','website','url','site','homepage','链接','連結'], isLink: true },
  { label: '购物地点', value: '', aliases: ['購物地點','購物地点','购物位置','購物位置','商場名稱','商场名称','商場','商场','mall','location'] },
  { label: '购物类型', value: '', aliases: ['購物類型','購物类型','類型','类型','category'] },
  { label: '特色推荐', value: '', aliases: ['特色推薦','特色','亮点','亮點','推薦','推荐'] },
  { label: '营业时间', value: '', aliases: ['營業時間','開放時間','开放时间','opening hours','hours','營業時段','营业时段'] },
  { label: '支付方式', value: '', aliases: ['付款方式','支付','支付類型','支付类型','付款','payment','payments','pay methods'] },
];

// Activity：建议时长、参考价位、网络平台、活动名称、活动类型、适合人群、体验特色、营业时间、预约要求、安全提示
const ACTIVITY_PLACEHOLDERS: PlaceholderRow[] = [
  { label: '建议时长', value: '', aliases: ['建議時長','duration','duration minutes','duration_minutes'] },
  { label: '参考价位', value: '', aliases: ['參考價位','人均','均消','price','price range','price_range','平均消费','平均消費'] },
  { label: '网络平台', value: '', aliases: ['網站','网站','网址','網址','官网','官網','link','website','url','site','homepage','链接','連結'], isLink: true },
  { label: '活动名称', value: '', aliases: ['活動名稱','名稱','名称','活動名','活动名'] },
  { label: '活动类型', value: '', aliases: ['活動類型','類型','类型','category'] },
  { label: '适合人群', value: '', aliases: ['適合人群','合適人群','适宜人群','對象','对象','人群'] },
  { label: '体验特色', value: '', aliases: ['體驗特色','特色','亮点','亮點','賣點','卖点'] },
  { label: '营业时间', value: '', aliases: ['營業時間','開放時間','开放时间','opening hours','hours','營業時段','营业时段'] },
  { label: '预约要求', value: '', aliases: ['預約要求','預約','预约','預訂','预订','booking required','reservation required'] },
  { label: '安全提示', value: '', aliases: ['安全須知','安全提示','注意事項','注意事项','safety','safety notes'] },
];

// Lodging：入住时间、退房时间、参考价位、电话、网络平台、住宿名称、住宿类型、住宿设施、特色服务、预订建议
const LODGING_PLACEHOLDERS: PlaceholderRow[] = [
  { label: '入住时间', value: '', aliases: ['入住時間','check-in','check in','checkin','check in time','check-in time'] },
  { label: '退房时间', value: '', aliases: ['退房時間','check-out','check out','checkout','check out time','check-out time'] },
  { label: '参考价位', value: '', aliases: ['參考價位','價格','价位','price','price range','price_range'] },
  { label: '电话', value: '', aliases: ['電話','phone','tel'] },
  { label: '网络平台', value: '', aliases: ['網站','网站','网址','網址','官网','官網','link','website','url','site','homepage','链接','連結'], isLink: true },
  { label: '住宿名称', value: '', aliases: ['住宿名稱','酒店名稱','住宿名','酒店名','name'] },
  { label: '住宿类型', value: '', aliases: ['住宿類型','住宿类别','住宿類別','hotel type','類型','类型'] },
  { label: '住宿设施', value: '', aliases: ['住宿設施','酒店設施','設施','设施','amenities'] },
  { label: '特色服务', value: '', aliases: ['特色服務','服務','服务','special services'] },
  { label: '预订建议', value: '', aliases: ['預訂建議','預約建議','预约建议','booking note','booking tips'] },
];

export const getDefaultPlaceholders = (kind: CardKind): PlaceholderRow[] => {
  const k = String(kind || '').toLowerCase();
  if (k === 'restaurant' || k === 'meal' || k === 'food') {
    return [...RESTAURANT_PLACEHOLDERS];
  }
  if (k === 'poi' || k === 'sight' || k === 'attraction') {
    return [...POI_PLACEHOLDERS];
  }
  if (k === 'shopping' || k === 'shop' || k === 'mall') {
    return [...SHOPPING_PLACEHOLDERS];
  }
  if (k === 'activity' || k === 'event' || k === 'experience') {
    return [...ACTIVITY_PLACEHOLDERS];
  }
  if (k === 'lodging' || k === 'hotel' || k === 'stay' || k === 'accommodation') {
    return [...LODGING_PLACEHOLDERS];
  }
  // fallback: minimal通用（给一个“网络平台”作为链接位）
  return [{ label: '网络平台', value: '', aliases: ['網站','网站','网址','網址','官网','官網','link','website','url','site','homepage','链接','連結'], isLink: true }];
};

// Merge placeholders into existing metaPairs (append missing rows at the end, do not overwrite values)
export const ensureMetaPlaceholders = (
  meta: Array<{ label: string; value: any }> | undefined | null,
  kind: CardKind
): Array<{ label: string; value: any }> => {
  const next = Array.isArray(meta) ? meta.slice() : [];
  const placeholders = getDefaultPlaceholders(kind);
  const exists = (lbl: string, aliases?: string[]) => {
    const all = [lbl, ...(aliases || [])];
    return next.some(p => all.some(a => String(p.label || '').trim().toLowerCase() === String(a).toLowerCase()));
  };
  placeholders.forEach(ph => {
    if (!exists(ph.label, ph.aliases)) {
      next.push({ label: ph.label, value: '' });
    }
  });
  return next;
};
